# Key concepts

## Data model

```mermaid Position objects
erDiagram
        PositionOpening ||--o{ PositionProfile : contains
```

A [`PositionOpening`] represents an open position at a hirer’s organization.
For example, a position opening may be created once a hirer approves a job requisition for a new role.

The `PositionOpening` holds some rudimentary metadata, including [contact details] and a [`statusCode`].
Representations of the position, such as a title and description displayed on a job board, are held by [`PositionProfile`s] within the opening:

- A [`PostedPositionProfile`] represents a job ad posted to the SEEK job board.

  Hirers may continue to post job ads within the same opening while the position remains unfilled.

  Grouping multiple job ads within a single opening is provided for your convenience.
  There is no business impact from grouping (or not grouping) job ads together currently.

  The position’s profile consists of:
  - Information on how to present the position opening to a candidate including its title, description and salary.

  - The location and job category of the position.
    These are displayed to the candidate and help determine the job ad’s price.

  - Posting instructions, including the SEEK ad product and [application method].

- An [`UnpostedPositionProfile`] contains information about a [position that hasn’t been posted to SEEK].

  Your software may share these with SEEK to support [Talent Search Connect].
  Grouping unposted positions with their related job ads helps SEEK match potential candidates against the opening.

While all job ads need to belong to a position opening,
your software can create a new position opening for each job ad.
Classic Job Posting provides a [`postPosition` mutation] to create a new position opening and post a job ad in a single operation.

The [SEEK Employer website] does not have an analogous concept of an open position currently.
Job ads posted through this channel will have a synthetic `PositionOpening` created for each of their `PositionProfile`s.
Synthetic position openings cannot be paginated, updated or deleted.

## Job ad lifecycle

```mermaid Job ad lifecycle
---
config:
  layout: elk
---
flowchart TD

  caja@{ shape: in-out, label: "Create on SEEK site" }
  post@{ shape: in-out, label: "Posting mutation" }
  seekEdit@{ shape: in-out, label: "Edit on SEEK site" }
  update@{ shape: in-out, label: "updatePostedPositionProfile" }
  seekExpire@{ shape: in-out, label: "Expire on SEEK site" }
  expire@{ shape: in-out, label: "End date reached" }
  close@{ shape: in-out, label: "closePostedPositionProfile" }
  event1@{ shape: event, label: "PositionProfilePosted" }
  event2@{ shape: event, label: "PositionProfileUpdated" }
  event3@{ shape: event, label: "PositionProfileClosed" }

  subgraph s1["Posting a job ad"]
    direction LR

    caja -- <1 minute --> live
    post -- <1 minute ---> live["Job ad goes live"]
    live -.-> event1
  end

  subgraph s2["Updating a job ad"]
    direction LR

    seekEdit --> updated
    update ---> updated["Job ad is updated"]
    updated -.-> event2
  end

  subgraph s3["Closing a job ad"]
    direction LR

    seekExpire --> closed
    expire --> closed["Job ad is closed"]
    close ---> closed
    closed -.-> event3
    closed -- 180 days ---> deleted["PositionProfile is deleted"]
  end

  s1 --> s2 --> s3
```

You can post a job ad by passing [`PositionProfile`] data to one of the posting mutations.
This starts an asynchronous process to place a live job ad on the SEEK job board.

The majority of job ads are automatically posted within a minute.
However, in certain circumstances SEEK will manually review a job ad before it’s posted.
In either case, a [`PositionProfilePosted` event] will be emitted when the job ad first goes live.

While the job ad is live,
[most](#update-restrictions) of its fields can be updated, with a [`PositionProfileUpdated` event] emitted.

The job ad will automatically close on its scheduled [`end` date].
If an end date wasn’t specified during posting, it will default to the maximum period of 30 days.
Scheduled closure is not instantaneous, and typically occurs within 24 hours.

You can explicitly close a job ad earlier using the [`closePostedPositionProfile` mutation].
Once the job ad is closed it will be removed from the SEEK job board and a [`PositionProfileClosed` event] will be emitted.

SEEK job ads are priced for a fixed time period of 30 days.
You can close a job ad earlier but the SEEK hirer will still be charged full price.

The `PositionProfile` object and its associated candidate applications will remain for 180 days after its close date.
Your software must store any data it needs to access after the 180 day period.

## Update restrictions

Job ads can be modified under their existing URL after they’ve been posted.
While most fields can be freely updated, there are some restrictions:

- Job ads cannot be moved between position openings or SEEK hirers.

- Updates to `positionTitle`, `jobCategories`, `positionLocation` and `postingInstructions.seekAdvertisementProductId` may result in an additional charge for that job ad.

  [Prices vary] by role and market factors.

- Updates to `seekApplicationQuestionnaireId` will fail with a [`BAD_USER_INPUT` error].

  For an equitable screening process,
  a SEEK job ad presents the same questionnaire to all applicants,
  and does not permit changes once the job ad has been posted.

- We do not recommend changing the [application method] of a job ad while it is live on the SEEK platform.

  Attempting to switch a live job ad between SEEK’s Apply Form and an external apply form may result in unexpected behaviour and complicate applicant tracking.
  You may support closing and re-posting the job ad to SEEK with a different [application method].

[`BAD_USER_INPUT` error]: /graphql/error-responses#bad_user_input
[`closePostedPositionProfile` mutation]: /schema/mutation/closePostedPositionProfile
[`end` date]: /schema/named-type/PostingInstruction#/field/end
[`PositionOpening`]: /schema/named-type/PositionOpening
[`PositionProfile`]: /schema/named-type/PositionProfile
[`PositionProfile`s]: /schema/named-type/PositionProfile
[`PositionProfileClosed` event]: /schema/webhooks/PositionProfileClosedEvent
[`PositionProfilePosted` event]: /schema/webhooks/PositionProfilePostedEvent
[`PositionProfileUpdated` event]: /schema/webhooks/PositionProfileUpdatedEvent
[`PostedPositionProfile`]: /schema/named-type/PostedPositionProfile
[`postPosition` mutation]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#postposition
[`statusCode`]: /schema/named-type/PositionOpening#/field/statusCode
[`UnpostedPositionProfile`]: /schema/named-type/UnpostedPositionProfile
[application method]: /use-cases/job-posting/application-methods
[contact details]: /use-cases/job-posting/contact-people
[position that hasn’t been posted to SEEK]: /use-cases/proactive-sourcing/unposted-positions
[prices vary]: https://help.au.employer.seek.com/s/article/How-much-does-it-cost-to-post-a-job-ad
[SEEK Employer website]: https://employer.seek.com
[Talent Search Connect]: /use-cases/proactive-sourcing
